import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

const TYPE_LABELS = {
  group:      { label: 'Group',      color: '#4a2080', text: '#c0a0ff' },
  individual: { label: 'Performer',  color: '#1a3a5c', text: '#99ccff' },
  costume:    { label: 'Costumer',   color: '#5c2a00', text: '#ffb366' },
  artist:     { label: 'Artist',     color: '#1a4a2a', text: '#90ee90' },
  event:      { label: 'Event',      color: '#5c1a1a', text: '#ff9999' }
};

function TypeBadge({ type }) {
  const t = TYPE_LABELS[type] || { label: type, color: '#333', text: '#ccc' };
  return (
    <span className="badge" style={{ background: t.color, color: t.text }}>
      {t.label.toUpperCase()}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = {
    pending:    'badge badge-pending',
    approved:   'badge badge-approved',
    rejected:   'badge badge-rejected',
    needs_info: 'badge badge-needs-info',
    banned:     'badge'
  }[status] || 'badge';
  return <span className={cls}>{(status || '').replace('_', ' ').toUpperCase()}</span>;
}

function AdminDashboard() {
  const [registrations, setRegistrations] = useState([]);
  const [allPending, setAllPending] = useState([]);
  const [selectedReg, setSelectedReg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [filter, setFilter] = useState('pending');
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminComment, setAdminComment] = useState('');

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAllPending();
    } else {
      fetchRegistrations();
    }
  }, [filter, activeTab]);

  const fetchAllPending = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/admin/all-pending', { headers: { Authorization: `Bearer ${token}` } });
      setAllPending(res.data);
    } catch (err) {
      setError('Failed to load queue: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const endpoint = filter === 'pending'
        ? `/api/admin/pending?type=${activeTab}`
        : `/api/admin/registrations?type=${activeTab}`;
      const res = await api.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      setRegistrations(res.data);
    } catch (err) {
      setError('Failed to load registrations: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchFullDetails = async (type, id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/admin/registration/${type}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedReg({ ...res.data, type });
      setEditData({ ...res.data });
      setAdminComment(res.data.admin_notes || '');
      setEditMode(false);
    } catch (err) {
      alert('Failed to load details: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/photos/${photoId}`, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh the detail view so the deleted photo disappears immediately
      fetchFullDetails(selectedReg.type, selectedReg.id);
    } catch (err) {
      alert('Failed to delete photo: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    if (!window.confirm(`Mark this as "${newStatus}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/admin/registration/${type}/${id}/status`,
        { status: newStatus, admin_notes: adminComment },
        { headers: { Authorization: `Bearer ${token}` } });
      setSelectedReg(null);
      if (activeTab === 'all') fetchAllPending();
      else fetchRegistrations();
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/admin/registration/${selectedReg.type}/${selectedReg.id}/edit`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } });
      setEditMode(false);
      fetchFullDetails(selectedReg.type, selectedReg.id);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const exportType = activeTab === 'all' ? 'groups' : activeTab;
      const res = await api.get(`/api/admin/export-csv?type=${exportType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `krampus-${exportType}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const renderField = (label, field, type = 'input', options = null) => (
    <div key={field} className="admin-field">
      <label className="admin-field-label">{label}</label>
      {editMode ? (
        type === 'textarea' ? (
          <textarea rows="4" value={editData[field] || ''} onChange={e => setEditData(p => ({ ...p, [field]: e.target.value }))} className="form-control" />
        ) : type === 'select' ? (
          <select value={editData[field] || ''} onChange={e => setEditData(p => ({ ...p, [field]: e.target.value }))} className="form-control">
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : type === 'checkbox' ? (
          <label className="checkbox-label">
            <input type="checkbox" checked={!!editData[field]} onChange={e => setEditData(p => ({ ...p, [field]: e.target.checked ? 1 : 0 }))} />
            <span>{label}</span>
          </label>
        ) : (
          <input type={type} value={editData[field] || ''} onChange={e => setEditData(p => ({ ...p, [field]: e.target.value }))} className="form-control" />
        )
      ) : (
        type === 'checkbox'
          ? <p className="admin-field-value">{editData[field] ? '✅ Yes' : '❌ No'}</p>
          : <p className="admin-field-value">{selectedReg?.[field] || '—'}</p>
      )}
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selectedReg) {
    const isGroup   = selectedReg.type === 'group';
    const isIndiv   = selectedReg.type === 'individual';
    const isCostume = selectedReg.type === 'costume';
    const isArtist  = selectedReg.type === 'artist';
    const isEvent   = selectedReg.type === 'event';

    return (
      <div className="admin-detail">
        <div className="admin-detail-header">
          <button onClick={() => setSelectedReg(null)} className="btn btn-secondary">← Back</button>
          <div className="admin-detail-header-actions">
            {!editMode ? (
              <button onClick={() => setEditMode(true)} className="btn btn-info btn-sm">✏️ Edit Submission</button>
            ) : (
              <>
                <button onClick={handleEditSave} className="btn btn-success btn-sm">💾 Save Changes</button>
                <button onClick={() => { setEditMode(false); setEditData({ ...selectedReg }); }} className="btn btn-secondary btn-sm">Cancel</button>
              </>
            )}
          </div>
        </div>

        <div className="admin-detail-card">
          <div className="admin-detail-title">
            <TypeBadge type={selectedReg.type} />
            <h1>{selectedReg.group_name || selectedReg.display_name || selectedReg.event_name}</h1>
            <StatusBadge status={selectedReg.status} />
          </div>

          {selectedReg.photos && selectedReg.photos.length > 0 && (
            <div className="admin-photos">
              {selectedReg.photos.map((photo, i) => (
                <div key={i} className="admin-photo-wrap">
                  <img src={`${UPLOADS_URL}/${photo.filename || photo}`} alt={`Photo ${i + 1}`} />
                  {photo.id && (
                    <button
                      className="admin-photo-delete-btn"
                      onClick={() => handleDeletePhoto(photo.id)}
                      title="Delete photo"
                    >✕</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {isGroup && (<>
            {renderField('Group Name', 'group_name')}
            {renderField('Primary Contact', 'primary_contact_name')}
            {renderField('Contact Email', 'primary_contact_email', 'email')}
            {renderField('Year Established', 'year_established')}
            {renderField('City', 'city')}
            {renderField('State', 'state')}
            {renderField('Geographic Description', 'geographic_description', 'textarea')}
            {renderField('Group Bio', 'group_bio', 'textarea')}
            {renderField('Event Types', 'event_types', 'textarea')}
            {renderField('Resources', 'resources', 'textarea')}
            {renderField('Accepts New Members', 'accepts_new_members')}
            {renderField('Membership Criteria', 'membership_criteria', 'textarea')}
            {renderField('Website', 'website')}
            {renderField('Facebook', 'facebook')}
            {renderField('Instagram', 'instagram')}
            {renderField('TikTok', 'tiktok')}
            {renderField('YouTube', 'youtube')}
            {renderField('Comments', 'comments', 'textarea')}
          </>)}

          {isIndiv && (<>
            {renderField('Display Name', 'display_name')}
            {renderField('Full Name', 'full_name')}
            {renderField('Email', 'email', 'email')}
            {renderField('Year Established', 'year_established')}
            {renderField('City', 'city')}
            {renderField('State', 'state')}
            {renderField('Affiliated Group', 'affiliated_group')}
            {renderField('Bio', 'bio', 'textarea')}
            {renderField('Event Types', 'event_types', 'textarea')}
            {renderField('Website', 'website')}
            {renderField('Facebook', 'facebook')}
            {renderField('Instagram', 'instagram')}
            {renderField('TikTok', 'tiktok')}
            {renderField('Comments', 'comments', 'textarea')}
          </>)}

          {isCostume && (<>
            {renderField('Display Name', 'display_name')}
            {renderField('Full Name', 'full_name')}
            {renderField('Email', 'email', 'email')}
            {renderField('Phone', 'phone')}
            {renderField('Country', 'country')}
            {renderField('City', 'city')}
            {renderField('State / Province', 'state')}
            {renderField('About / What They Do', 'bio', 'textarea')}
            {renderField('Product Description', 'products_description', 'textarea')}
            {renderField('How to Purchase', 'how_to_purchase', 'textarea')}
            {renderField('Product Types', 'product_types')}
            {renderField('Commission Status', 'commission_status', 'select', ['Open', 'Closed', 'Waitlist'])}
            {renderField('Price Range', 'price_range', 'select', ['', 'Budget', 'Mid-range', 'Premium'])}
            {renderField('Turnaround Time', 'turnaround_time')}
            {renderField('Ships To', 'ships_to')}
            {renderField('Website', 'website')}
            {renderField('Facebook', 'facebook')}
            {renderField('Instagram', 'instagram')}
            {renderField('TikTok', 'tiktok')}
            {renderField('YouTube', 'youtube')}
            {renderField('Comments', 'comments', 'textarea')}
          </>)}

          {isArtist && (<>
            {renderField('Display Name', 'display_name')}
            {renderField('Full Name', 'full_name')}
            {renderField('Email', 'email', 'email')}
            {renderField('Phone', 'phone')}
            {renderField('Country', 'country')}
            {renderField('City', 'city')}
            {renderField('State / Province', 'state')}
            {renderField('Artist Bio', 'bio', 'textarea')}
            {renderField('Work Description', 'products_description', 'textarea')}
            {renderField('How to Purchase', 'how_to_purchase', 'textarea')}
            {renderField('Art Types', 'art_types')}
            {renderField('Medium / Materials', 'medium')}
            {renderField('Style Description', 'style_description', 'textarea')}
            {renderField('Ships Physical Work', 'ships_physical', 'select', ['No', 'Yes'])}
            {renderField('Accepts Custom Requests', 'accepts_custom_requests', 'select', ['No', 'Yes'])}
            {renderField('Uses AI in Artwork', 'uses_ai', 'checkbox')}
            {renderField('Commission Status', 'commission_status', 'select', ['Open', 'Closed', 'Waitlist'])}
            {renderField('Shop Link', 'shop_link')}
            {renderField('Price Range', 'price_range', 'select', ['', 'Budget', 'Mid-range', 'Premium'])}
            {renderField('Turnaround Time', 'turnaround_time')}
            {renderField('Ships To', 'ships_to')}
            {renderField('Website', 'website')}
            {renderField('Facebook', 'facebook')}
            {renderField('Instagram', 'instagram')}
            {renderField('TikTok', 'tiktok')}
            {renderField('YouTube', 'youtube')}
            {renderField('Comments', 'comments', 'textarea')}
          </>)}

          {isEvent && (<>
            {renderField('Event Name', 'event_name')}
            {renderField('Organized By', 'organized_by')}
            {renderField('End Date', 'event_end_date', 'date')}
            {renderField('Start Time', 'event_start_time', 'time')}
            {renderField('End Time', 'event_end_time', 'time')}
            {renderField('Location Name', 'event_location_name')}
            {renderField('Location Address', 'event_location_address')}
            {renderField('City', 'event_city')}
            {renderField('State', 'event_state')}
            {renderField('Website / Social Link', 'website_link')}
            {renderField('Event Details', 'event_details', 'textarea')}
          </>)}

          <div className="admin-comment-section">
            <h3>Admin Comment to Applicant</h3>
            <p className="admin-comment-hint">
              Visible to the applicant in their dashboard when status is set to "Needs Info".
            </p>
            <textarea value={adminComment} onChange={e => setAdminComment(e.target.value)} rows="4"
              placeholder="Enter your message to the applicant here..."
              className="form-control" />
          </div>

          <div className="admin-action-bar">
            {selectedReg.status !== 'approved' && (
              <button onClick={() => handleStatusChange(selectedReg.type, selectedReg.id, 'approved')}
                className="btn btn-success">
                ✓ Approve &amp; Publish
              </button>
            )}
            <button onClick={() => handleStatusChange(selectedReg.type, selectedReg.id, 'rejected')}
              className="btn btn-danger">
              ✗ Reject
            </button>
            <button onClick={() => handleStatusChange(selectedReg.type, selectedReg.id, 'needs_info')}
              className="btn btn-info">
              ℹ️ Needs Info
            </button>
            {selectedReg.status === 'approved' && (
              <button onClick={() => handleStatusChange(selectedReg.type, selectedReg.id, 'pending')}
                className="btn btn-secondary">
                ↩ Move to Pending
              </button>
            )}
            <button onClick={() => handleStatusChange(selectedReg.type, selectedReg.id, 'banned')}
              className="btn btn-gray">
              🚫 Ban
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'all',         label: '📋 All Pending' },
    { id: 'groups',      label: 'Groups'      },
    { id: 'individuals', label: 'Performers'  },
    { id: 'costumers',   label: 'Costumers'   },
    { id: 'artists',     label: 'Artists'     },
    { id: 'events',      label: 'Events'      }
  ];

  const displayList = activeTab === 'all' ? allPending : registrations;

  return (
    <div className="admin-detail">
      <h1>Admin Dashboard</h1>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="admin-tabs">
        {TABS.map(tab => (
          <button key={tab.id} className={`admin-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSelectedReg(null); }}>
            {tab.label}
            {tab.id === 'all' && allPending.length > 0 && (
              <span className="badge badge-danger badge-count">
                {allPending.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab !== 'all' && (
        <div className="filter-bar">
          <div className="filter-bar-left">
            {['pending', 'all'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}>
                {f === 'pending' ? '📋 Pending' : '📁 All'}
              </button>
            ))}
          </div>
          <button onClick={handleExportCSV} className="btn btn-success btn-sm">
            ⬇️ Export CSV
          </button>
        </div>
      )}

      {activeTab === 'all' && (
        <p className="queue-count">
          Showing all pending and needs-info submissions across all categories, oldest first.
        </p>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : displayList.length === 0 ? (
        <div className="queue-empty">
          {activeTab === 'all'
            ? <p>✅ All clear — no pending submissions.</p>
            : <p>No {filter === 'pending' ? 'pending' : ''} {activeTab} at this time.</p>
          }
        </div>
      ) : (
        <div>
          <p className="queue-count">
            {displayList.length} record{displayList.length !== 1 ? 's' : ''}
          </p>
          {displayList.map(reg => (
            <div key={reg.id + reg.type}
              className="queue-row"
              style={{ borderLeftColor: TYPE_LABELS[reg.type]?.color || '#555' }}>
              <div className="queue-row-inner">
                <div className="queue-row-info">
                  <div className="queue-row-badges">
                    <TypeBadge type={reg.type} />
                    <StatusBadge status={reg.status} />
                    {reg.is_resubmission && (
                      <span className="badge badge-resubmission">✏️ EDIT / RESUBMISSION</span>
                    )}
                  </div>
                  <h3 className="queue-row-name">{reg.name}</h3>
                  <div className="queue-row-meta">
                    <span className="queue-row-date">
                      🕐 Submitted: {new Date(reg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {reg.is_resubmission && (
                      <span className="queue-row-date">
                        ✏️ Last edited: {new Date(reg.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="queue-row-actions">
                  <button onClick={() => fetchFullDetails(reg.type, reg.id)}
                    className="btn btn-info btn-sm">
                    👁 View / Edit
                  </button>
                  <button onClick={() => handleStatusChange(reg.type, reg.id, 'approved')}
                    className="btn btn-success btn-sm">
                    ✓ Approve
                  </button>
                  <button onClick={() => handleStatusChange(reg.type, reg.id, 'rejected')}
                    className="btn btn-danger btn-sm">
                    ✗ Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
